import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Fade,
  Stack,
  Typography,
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import AppButton from "../../components/button/AppButton";
import AppTextInput from "../../components/inputs/AppTextInput";
import { AppColors } from "../../constants/AppColors";
import { useLoginScreen } from "./hooks/useLoginScreen";
import GoogleAuthButton from "../../components/button/GoogleAuthButton";

const FacebookIcon = () => (
  <Box sx={{ color: "#fff", fontWeight: 900, fontSize: 38, lineHeight: 1 }}>
    f
  </Box>
);

const LoginScreen = () => {
  const vm = useLoginScreen();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: AppColors.background,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Fade in timeout={350}>
        <Card sx={{ width: "100%", maxWidth: 430, borderRadius: "24px" }}>
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "18px",
                bgcolor: AppColors.primary,
                color: AppColors.textOnPrimary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                fontWeight: 900,
                fontSize: 24,
              }}
            >
              RP
            </Box>

            <Typography
              variant="h4"
              sx={{ fontWeight: 900, color: AppColors.textPrimary, mb: 1 }}
            >
              RentPilot
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.5 }}
            >
              {vm.t("auth.signInSubtitle")}
            </Typography>

            {vm.error ? (
              <Typography color="error" sx={{ fontWeight: 700, mb: 1.5 }}>
                {vm.error}
              </Typography>
            ) : null}

            <Box component="form" onSubmit={vm.handleLogin}>
              <AppTextInput
                label={vm.t("auth.email")}
                value={vm.email}
                onChange={(event) => vm.setEmail(event.target.value)}
                placeholder={vm.t("auth.email")}
                autoComplete="email"
              />

              <AppTextInput
                label={vm.t("auth.password")}
                value={vm.password}
                onChange={(event) => vm.setPassword(event.target.value)}
                placeholder={vm.t("auth.password")}
                type="password"
                autoComplete="current-password"
              />

              <Box sx={{ mt: 0.5, textAlign: "right" }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={vm.goToForgotPassword}
                  disabled={vm.loading || vm.socialLoading}
                >
                  {vm.t("auth.forgotPassword")}
                </Button>
              </Box>

              <Box sx={{ mt: 1 }}>
                <AppButton
                  title={vm.t("auth.login")}
                  type="submit"
                  loading={vm.loading}
                  disabled={vm.socialLoading}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 2 }}>{vm.t("auth.or")}</Divider>

            <Stack
              direction="row"
              spacing={3}
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box sx={{ position: "relative", width: 74, height: 74 }}>
                <GoogleAuthButton
                  loading={vm.socialLoading}
                  disabled={vm.loading}
                  onClick={() => {
                    const button = document.querySelector(
                      "#hidden-google-login div[role='button']",
                    ) as HTMLElement | null;

                    button?.click();
                  }}
                />

                <Box
                  id="hidden-google-login"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                >
                  <GoogleLogin
                    onSuccess={(response) =>
                      vm.handleGoogleLoginSuccess(response.credential)
                    }
                    onError={() => console.log("Google login failed")}
                  />
                </Box>
              </Box>

              <Button
                onClick={vm.handleFacebookLogin}
                disabled={vm.loading || vm.socialLoading}
                sx={{
                  width: 74,
                  height: 74,
                  minWidth: 74,
                  borderRadius: "50%",
                  bgcolor: "#1877F2",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#166FE5",
                  },
                }}
              >
                <FacebookIcon />
              </Button>
            </Stack>

            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {vm.t("auth.noAccount")}
              </Typography>

              <Button
                variant="text"
                onClick={vm.goToRegister}
                disabled={vm.loading || vm.socialLoading}
              >
                {vm.t("auth.createAccount")}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
};

export default LoginScreen;
